'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  const navigationItems = [
  { label: 'Home', href: '/homepage' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Portfolio', href: '/portfolio', hidden: true },
  { label: 'Journal', href: '/journal', hidden: true }];

  const moreMenuItems = [
  { label: 'Brand Guidelines', href: '/brand-guidelines', hidden: true },
  { label: 'Archive', href: '/archive', hidden: true }];

  // Helper function to check if a link is active
  const isActiveLink = (href: string) => pathname === href;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-fast ${
      isScrolled ?
      'bg-secondary/90 backdrop-blur-[10px] shadow-subtle' :
      'bg-transparent'} ${
      className}`}>

      <div className="w-full px-6 lg:px-12">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link
            href="/homepage"
            className="flex items-center group transition-opacity duration-fast hover:opacity-70">

            <img
              src="/assets/images/Transparent_V-1766978963972.png"
              alt="Valinaire Logo"
              width="48"
              height="48"
              className="transition-transform duration-fast group-hover:scale-105"
            />
            <span className="ml-3 text-xl font-headline font-bold tracking-tight text-primary">Valinaire
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationItems.filter(item => !item.hidden).map((item) => {
              const isActive = isActiveLink(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`relative text-sm font-cta font-semibold tracking-wide transition-opacity duration-fast group ${
                  isActive ?
                  'text-primary opacity-100' : 'text-primary hover:opacity-70'}`
                  }>

                  {item.label}
                  <span
                    className={`absolute bottom-0 left-0 h-[2px] bg-primary transition-all duration-fast ${
                    isActive ?
                    'w-full' : 'w-0 group-hover:w-full'}`
                    } />

                </Link>);

            })}

            {/* More Dropdown */}
            {moreMenuItems.filter(item => !item.hidden).length > 0 && (
            <div className="relative group">
              <button className="flex items-center text-sm font-cta font-semibold tracking-wide text-primary transition-opacity duration-fast hover:opacity-70">
                More
                <Icon name="ChevronDownIcon" size={16} className="ml-1" />
              </button>
              <div className="absolute top-full right-0 mt-2 w-48 bg-secondary border border-border shadow-card opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-fast">
                {moreMenuItems.filter(item => !item.hidden).map((item) =>
                <Link
                  key={item.href}
                  href={item.href}
                  className="block px-4 py-3 text-sm font-body text-primary hover:bg-muted transition-colors duration-fast">

                    {item.label}
                  </Link>
                )}
              </div>
            </div>
            )}

            {/* CTA Button */}
            <Link
              href="/contact"
              className="px-6 py-3 bg-primary text-primary-foreground text-sm font-cta font-semibold tracking-wide border border-primary transition-all duration-fast hover:-translate-y-0.5 hover:shadow-card">

              Get in Touch
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-primary transition-opacity duration-fast hover:opacity-70"
            aria-label="Toggle menu">

            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24} />

          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 top-20 bg-secondary z-40 transition-all duration-medium ${
        isMobileMenuOpen ?
        'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`
        }>

        <nav className="flex flex-col px-6 py-8 space-y-6">
          {navigationItems.filter(item => !item.hidden).map((item, index) => {
            const isActive = isActiveLink(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-2xl font-headline font-bold transition-opacity duration-fast ${
                isActive ?
                'text-primary opacity-100 underline decoration-2 underline-offset-4' : 'text-primary hover:opacity-70'}`
                }
                style={{
                  animation: isMobileMenuOpen ?
                  `slide-in-left 0.4s ease-out ${index * 0.1}s both` :
                  'none'
                }}>

                {item.label}
              </Link>);

          })}

          {moreMenuItems.filter(item => !item.hidden).length > 0 && (
          <div className="pt-4 border-t border-border">
            <p className="text-xs font-cta font-semibold tracking-wide text-muted-foreground mb-4">
              MORE
            </p>
            {moreMenuItems.filter(item => !item.hidden).map((item, index) => {
              const isActive = isActiveLink(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-3 text-lg font-body transition-opacity duration-fast ${
                  isActive ?
                  'text-primary opacity-100 font-semibold' : 'text-primary hover:opacity-70'}`
                  }
                  style={{
                    animation: isMobileMenuOpen ?
                    `slide-in-left 0.4s ease-out ${
                    (navigationItems.filter(item => !item.hidden).length + index) * 0.1}s both` :

                    'none'
                  }}>

                  {item.label}
                </Link>);

            })}
          </div>
          )}

          <Link
            href="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className="inline-block px-8 py-4 bg-primary text-primary-foreground text-base font-cta font-semibold tracking-wide text-center border border-primary transition-all duration-fast hover:-translate-y-0.5 hover:shadow-card mt-8"
            style={{
              animation: isMobileMenuOpen ?
              `fade-in 0.4s ease-out ${
              (navigationItems.filter(item => !item.hidden).length + moreMenuItems.filter(item => !item.hidden).length) * 0.1}s both` :

              'none'
            }}>

            Get in Touch
          </Link>
        </nav>
      </div>
    </header>);

};

export default Header;